import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/layout/Layout';
import { TicketModal } from '../../../../components/support/TicketModal';

interface Participante {
  id: string;
  nome: string;
  email: string;
  departamento: string;
  cargo: string;
  pontos: number;
  nivel: string;
  avatar: string;
  atendimentos: number;
  dataIngresso: Date;
  ultimaAtividade: Date;
  cpf?: string;
  cnpj?: string;
  statusCampanha: 'Pendente' | 'Ativo' | 'Inativo' | 'Suspenso';
  publicoAlvo: 'PF' | 'PJ' | 'MEI' | 'CLT' | 'AUTONOMO' | 'REPRESENTANTE' | 'DISTRIBUIDOR';
  campanhas: Array<{
    id: number;
    nome: string;
    tipo: string;
    status: string;
    nivelParticipante: string;
    pontosAcumulados: number;
    metaAtingida: number;
    posicaoRanking: number;
    distintivos: Array<{
      nome: string;
      icone: string;
      cor: string;
    }>;
    missoesConcluidas: number;
    missoesTotais: number;
    dataInicio: string;
    dataFim: string;
    atendimentos: number;
    statusParticipacao: 'Ativo' | 'Pausado' | 'Concluído';
    // NOVOS CAMPOS
    convites: {
      enviados: number;
      aceitos: number;
    };
    comunicacao: {
      abertura: number;
      resposta: number;
    };
    contratos: {
      assinados: number;
      pendentes: number;
    };
  }>;
  premiacoes: Array<{
    id: string;
    data: Date;
    tipo: 'Gift Card' | 'Dinheiro' | 'Wallet' | 'Cartão Pré-pago' | 'Produto Físico';
    valor: number;
    descricao: string;
    campanha: string;
    status: 'Processado' | 'Pendente' | 'Entregue';
  }>;
}

export default function ParticipantesListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroDepartamento, setFiltroDepartamento] = useState('todos');
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [showCampanhasModal, setShowCampanhasModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConvitesModal, setShowConvitesModal] = useState(false);
  const [participanteSelecionado, setParticipanteSelecionado] = useState<Participante | null>(null);
  const [expandedParticipante, setExpandedParticipante] = useState<string | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'campanhas' | 'comunicacao' | 'atendimento'>('geral');
  const [showJourney, setShowJourney] = useState(true);
  const [tipoParticipante, setTipoParticipante] = useState<'fisica' | 'juridica'>('fisica');
  const [participantesSelecionados, setParticipantesSelecionados] = useState<string[]>([]);
  const [tipoConvite, setTipoConvite] = useState<'todos' | 'selecionados'>('todos');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [nomeModelo, setNomeModelo] = useState('');
  const [modelosSalvos, setModelosSalvos] = useState<Array<{
    id: string;
    nome: string;
    assunto: string;
    mensagemEmail: string;
    mensagemSMS: string;
    dataCriacao: Date;
  }>>([
    {
      id: '1',
      nome: 'Convite Padrão',
      assunto: 'Convite para Participar do Programa de Incentivo',
      mensagemEmail: `Olá [NOME],

Você foi convidado(a) para participar do nosso programa de incentivo!

Este é um programa exclusivo onde você poderá:
• Acumular pontos através de suas atividades
• Participar de campanhas especiais
• Concorrer a prêmios incríveis
• Acompanhar seu desempenho em tempo real

Para aceitar o convite e começar a participar, clique no link abaixo:
[LINK_CONVITE]

Qualquer dúvida, entre em contato conosco.

Atenciosamente,
Equipe do Programa de Incentivo`,
      mensagemSMS: 'Olá [NOME]! Você foi convidado para nosso programa de incentivo. Acesse: [LINK_CONVITE]',
      dataCriacao: new Date('2024-01-15')
    },
    {
      id: '2',
      nome: 'Convite Urgente',
      assunto: 'Última Chance - Participe do Programa de Incentivo',
      mensagemEmail: `Olá [NOME],

Esta é sua última chance de participar do nosso programa de incentivo!

Não perca a oportunidade de fazer parte deste programa exclusivo.

Acesse agora: [LINK_CONVITE]

Equipe do Programa de Incentivo`,
      mensagemSMS: '[NOME], última chance! Participe do programa de incentivo: [LINK_CONVITE]',
      dataCriacao: new Date('2024-01-18')
    }
  ]);
  const [modeloSelecionado, setModeloSelecionado] = useState<string>('');
  const [conviteData, setConviteData] = useState({
    assunto: 'Convite para Participar do Programa de Incentivo',
    mensagemEmail: `Olá [NOME],

Você foi convidado(a) para participar do nosso programa de incentivo!

Este é um programa exclusivo onde você poderá:
• Acumular pontos através de suas atividades
• Participar de campanhas especiais
• Concorrer a prêmios incríveis
• Acompanhar seu desempenho em tempo real

Para aceitar o convite e começar a participar, clique no link abaixo:
[LINK_CONVITE]

Qualquer dúvida, entre em contato conosco.

Atenciosamente,
Equipe do Programa de Incentivo`,
    mensagemSMS: 'Olá [NOME]! Você foi convidado para nosso programa de incentivo. Acesse: [LINK_CONVITE]',
    incluirLink: true,
    enviarPorEmail: true,
    enviarPorSMS: false,
    enviarPorWhatsApp: false,
    agendarEnvio: false,
    dataAgendamento: '',
    horaAgendamento: ''
  });

  // Estado para o carousel
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 5; // Total de cards na jornada

  // Funções de navegação do carousel
  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const [campanhasSelecionadas, setCampanhasSelecionadas] = useState<number[]>([]);

  // Mock de campanhas disponíveis
  const campanhasDisponiveis = [
    { id: 1, nome: 'Vendas Q4 2024', tipo: 'Vendas', status: 'Ativa', participantes: 8 },
    { id: 2, nome: 'Meta Dezembro', tipo: 'Performance', status: 'Ativa', participantes: 7 },
    { id: 3, nome: 'Captação Novos Clientes', tipo: 'Aquisição', status: 'Ativa', participantes: 5 },
    { id: 4, nome: 'Capacitação Equipe', tipo: 'Capacitação', status: 'Planejada', participantes: 0 },
    { id: 5, nome: 'Retenção de Clientes', tipo: 'Retenção', status: 'Ativa', participantes: 6 }
  ];

  const handleSelecionarCampanha = (campanhaId: number) => {
    if (campanhasSelecionadas.includes(campanhaId)) {
      setCampanhasSelecionadas(campanhasSelecionadas.filter(id => id !== campanhaId));
    } else {
      setCampanhasSelecionadas([...campanhasSelecionadas, campanhaId]);
    }
  };

  const handleSelecionarTodasCampanhas = () => {
    if (campanhasSelecionadas.length === campanhasDisponiveis.length) {
      setCampanhasSelecionadas([]);
    } else {
      setCampanhasSelecionadas(campanhasDisponiveis.map(c => c.id));
    }
  };

  const [formData, setFormData] = useState({
    // Dados Básicos
    tipoParticipante: 'fisica',
    nome: '',
    email: '',
    telefone: '',
    celular: '',
    
    // Pessoa Física
    cpf: '',
    rg: '',
    dataNascimento: '',
    estadoCivil: '',
    profissao: '',
    
    // Pessoa Jurídica
    cnpj: '',
    inscricaoEstadual: '',
    razaoSocial: '',
    nomeFantasia: '',
    porte: '',
    ramoAtividade: '',
    
    // Endereço
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'Brasil',
    
    // Dados Bancários
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: '',
    chavePix: '',
    
    // Dados Profissionais
    departamento: '',
    cargo: '',
    dataAdmissao: '',
    salario: '',
    supervisor: '',
    
    // Informações do Programa
    nivelInicial: 'Bronze',
    pontosIniciais: '0',
    metaIndividual: '',
    observacoes: '',
    
    // Configurações
    receberNotificacoes: true,
    receberEmail: true,
    receberSMS: false,
    participarRanking: true,
    
    // Documentos
    documentos: [] as File[]
  });

  const mockParticipantes: Participante[] = [
    {
      id: '1',
      nome: 'Ana Silva',
      email: 'ana.silva@empresa.com',
      departamento: 'Vendas',
      cargo: 'Consultora de Vendas',
      pontos: 2450,
      nivel: 'Ouro',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20consultant%20smiling%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=ana1&orientation=squarish',
      atendimentos: 3,
      dataIngresso: new Date('2024-01-15'),
      ultimaAtividade: new Date('2024-01-20'),
      cpf: '123.456.789-01',
      statusCampanha: 'Pendente',
      publicoAlvo: 'PF',
      campanhas: [
        {
          id: 1,
          nome: 'Vendas Q4 2024',
          tipo: 'Vendas',
          status: 'Ativa',
          nivelParticipante: 'Ouro',
          pontosAcumulados: 1250,
          metaAtingida: 75,
          posicaoRanking: 3,
          distintivos: [
            { nome: 'Top Vendedor', icone: 'ri-trophy-line', cor: 'text-yellow-500' },
            { nome: 'Consistência', icone: 'ri-medal-line', cor: 'text-blue-500' }
          ],
          missoesConcluidas: 8,
          missoesTotais: 10,
          dataInicio: '01/10/2024',
          dataFim: '31/12/2024',
          atendimentos: 2,
          statusParticipacao: 'Ativo',
          convites: { enviados: 3, aceitos: 2 },
          comunicacao: { abertura: 15, resposta: 8 },
          contratos: { assinados: 2, pendentes: 0 }
        },
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Prata',
          pontosAcumulados: 890,
          metaAtingida: 60,
          posicaoRanking: 5,
          distintivos: [
            { nome: 'Dedicação', icone: 'ri-star-line', cor: 'text-purple-500' }
          ],
          missoesConcluidas: 6,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 1,
          statusParticipacao: 'Ativo',
          convites: { enviados: 2, aceitos: 2 },
          comunicacao: { abertura: 12, resposta: 5 },
          contratos: { assinados: 1, pendentes: 1 }
        },
        {
          id: 3,
          nome: 'Captação Novos Clientes',
          tipo: 'Aquisição',
          status: 'Ativa',
          nivelParticipante: 'Bronze',
          pontosAcumulados: 310,
          metaAtingida: 40,
          posicaoRanking: 8,
          distintivos: [],
          missoesConcluidas: 4,
          missoesTotais: 10,
          dataInicio: '15/11/2024',
          dataFim: '15/01/2025',
          atendimentos: 0,
          statusParticipacao: 'Ativo',
          convites: { enviados: 1, aceitos: 1 },
          comunicacao: { abertura: 8, resposta: 3 },
          contratos: { assinados: 0, pendentes: 1 }
        }
      ],
      premiacoes: [
        {
          id: 'p1',
          data: new Date('2024-01-18'),
          tipo: 'Gift Card',
          valor: 500,
          descricao: 'Gift Card Amazon - Bônus Meta Mensal',
          campanha: 'Vendas Q4 2024',
          status: 'Processado'
        },
        {
          id: 'p2',
          data: new Date('2024-01-15'),
          tipo: 'Dinheiro',
          valor: 1200,
          descricao: 'Bônus de Performance',
          campanha: 'Meta Dezembro',
          status: 'Entregue'
        },
        {
          id: 'p3',
          data: new Date('2024-01-10'),
          tipo: 'Wallet',
          valor: 300,
          descricao: 'Crédito Digital - Missão Concluída',
          campanha: 'Vendas Q4 2024',
          status: 'Entregue'
        },
        {
          id: 'p4',
          data: new Date('2024-01-05'),
          tipo: 'Cartão Pré-pago',
          valor: 800,
          descricao: 'Cartão Vale Alimentação',
          campanha: 'Meta Dezembro',
          status: 'Entregue'
        },
        {
          id: 'p5',
          data: new Date('2024-01-02'),
          tipo: 'Produto Físico',
          valor: 2500,
          descricao: 'Notebook Dell Inspiron 15',
          campanha: 'Vendas Q4 2024',
          status: 'Entregue'
        }
      ]
    },
    {
      id: '2',
      nome: 'Carlos Eduardo Lima',
      email: 'carlos.lima@empresa.com',
      departamento: 'Marketing',
      cargo: 'Analista de Marketing',
      pontos: 1890,
      nivel: 'Prata',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20marketing%20analyst%20confident%20business%20suit%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=carlos2&orientation=squarish',
      atendimentos: 7,
      dataIngresso: new Date('2024-01-10'),
      ultimaAtividade: new Date('2024-01-19'),
      cpf: '987.654.321-02',
      statusCampanha: 'Pendente',
      publicoAlvo: 'CLT',
      campanhas: [
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Ouro',
          pontosAcumulados: 1890,
          metaAtingida: 85,
          posicaoRanking: 2,
          distintivos: [
            { nome: 'Inovador', icone: 'ri-lightbulb-line', cor: 'text-orange-500' },
            { nome: 'Colaborador', icone: 'ri-team-line', cor: 'text-green-500' }
          ],
          missoesConcluidas: 9,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 7,
          statusParticipacao: 'Ativo',
          convites: { enviados: 4, aceitos: 3 },
          comunicacao: { abertura: 22, resposta: 14 },
          contratos: { assinados: 3, pendentes: 0 }
        }
      ],
      premiacoes: [
        {
          id: 'p6',
          data: new Date('2024-01-19'),
          tipo: 'Dinheiro',
          valor: 1500,
          descricao: 'Bônus Trimestral',
          campanha: 'Meta Dezembro',
          status: 'Processado'
        },
        {
          id: 'p7',
          data: new Date('2024-01-12'),
          tipo: 'Gift Card',
          valor: 400,
          descricao: 'Gift Card Magazine Luiza',
          campanha: 'Meta Dezembro',
          status: 'Entregue'
        }
      ]
    },
    {
      id: '3',
      nome: 'Mariana Santos',
      email: 'mariana.santos@empresa.com',
      departamento: 'RH',
      cargo: 'Coordenadora de RH',
      pontos: 3200,
      nivel: 'Diamante',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20HR%20coordinator%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=mariana3&orientation=squarish',
      atendimentos: 2,
      dataIngresso: new Date('2024-01-05'),
      ultimaAtividade: new Date('2024-01-20'),
      cpf: '456.789.123-03',
      statusCampanha: 'Ativo',
      publicoAlvo: 'CLT',
      campanhas: [
        {
          id: 1,
          nome: 'Vendas Q4 2024',
          tipo: 'Vendas',
          status: 'Ativa',
          nivelParticipante: 'Diamante',
          pontosAcumulados: 3200,
          metaAtingida: 95,
          posicaoRanking: 1,
          distintivos: [
            { nome: 'Líder', icone: 'ri-vip-crown-line', cor: 'text-purple-500' },
            { nome: 'Top Vendedor', icone: 'ri-trophy-line', cor: 'text-yellow-500' },
            { nome: 'Excelência', icone: 'ri-award-line', cor: 'text-blue-500' }
          ],
          missoesConcluidas: 10,
          missoesTotais: 10,
          dataInicio: '01/10/2024',
          dataFim: '31/12/2024',
          atendimentos: 2,
          statusParticipacao: 'Ativo',
          convites: { enviados: 5, aceitos: 5 },
          comunicacao: { abertura: 28, resposta: 20 },
          contratos: { assinados: 4, pendentes: 0 }
        }
      ]
    },
    {
      id: '4',
      nome: 'Pedro Oliveira',
      email: 'pedro.oliveira@empresa.com',
      departamento: 'TI',
      cargo: 'Desenvolvedor',
      pontos: 1650,
      nivel: 'Bronze',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20developer%20programmer%20confident%20business%20casual%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=pedro4&orientation=squarish',
      atendimentos: 12,
      dataIngresso: new Date('2024-01-12'),
      ultimaAtividade: new Date('2024-01-18'),
      cpf: '789.123.456-04',
      statusCampanha: 'Pendente',
      publicoAlvo: 'PF',
      campanhas: []
    },
    {
      id: '5',
      nome: 'Juliana Rodrigues',
      email: 'juliana.rodrigues@empresa.com',
      departamento: 'Financeiro',
      cargo: 'Analista Financeiro',
      pontos: 2100,
      nivel: 'Prata',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20financial%20analyst%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=juliana5&orientation=squarish',
      atendimentos: 5,
      dataIngresso: new Date('2024-01-08'),
      ultimaAtividade: new Date('2024-01-19'),
      cpf: '321.654.987-05',
      statusCampanha: 'Pendente',
      publicoAlvo: 'MEI',
      campanhas: [
        {
          id: 3,
          nome: 'Captação Novos Clientes',
          tipo: 'Aquisição',
          status: 'Ativa',
          nivelParticipante: 'Prata',
          pontosAcumulados: 2100,
          metaAtingida: 70,
          posicaoRanking: 4,
          distintivos: [
            { nome: 'Persistente', icone: 'ri-fire-line', cor: 'text-red-500' }
          ],
          missoesConcluidas: 7,
          missoesTotais: 10,
          dataInicio: '15/11/2024',
          dataFim: '15/01/2025',
          atendimentos: 5,
          statusParticipacao: 'Ativo',
          convites: { enviados: 2, aceitos: 1 },
          comunicacao: { abertura: 10, resposta: 6 },
          contratos: { assinados: 1, pendentes: 1 }
        },
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Bronze',
          pontosAcumulados: 450,
          metaAtingida: 45,
          posicaoRanking: 12,
          distintivos: [],
          missoesConcluidas: 5,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 5,
          statusParticipacao: 'Ativo',
          convites: { enviados: 1, aceitos: 1 },
          comunicacao: { abertura: 7, resposta: 3 },
          contratos: { assinados: 0, pendentes: 1 }
        }
      ]
    },
    {
      id: '6',
      nome: 'Fernanda Lima',
      email: 'fernanda.lima@empresa.com',
      departamento: 'Marketing',
      cargo: 'Supervisora de Marketing',
      pontos: 2800,
      nivel: 'Ouro',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20marketing%20supervisor%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=fernanda6&orientation=squarish',
      atendimentos: 8,
      dataIngresso: new Date('2024-01-03'),
      ultimaAtividade: new Date('2024-01-20'),
      cpf: '654.987.321-06',
      statusCampanha: 'Pendente',
      publicoAlvo: 'CLT',
      campanhas: [
        {
          id: 1,
          nome: 'Vendas Q4 2024',
          tipo: 'Vendas',
          status: 'Ativa',
          nivelParticipante: 'Ouro',
          pontosAcumulados: 2800,
          metaAtingida: 80,
          posicaoRanking: 2,
          distintivos: [
            { nome: 'Criativo', icone: 'ri-palette-line', cor: 'text-pink-500' }
          ],
          missoesConcluidas: 8,
          missoesTotais: 10,
          dataInicio: '01/10/2024',
          dataFim: '31/12/2024',
          atendimentos: 8,
          statusParticipacao: 'Ativo',
          convites: { enviados: 3, aceitos: 2 },
          comunicacao: { abertura: 18, resposta: 11 },
          contratos: { assinados: 2, pendentes: 1 }
        }
      ]
    },
    {
      id: '7',
      nome: 'Roberto Costa',
      email: 'roberto.costa@empresa.com',
      departamento: 'Vendas',
      cargo: 'Gerente de Vendas',
      pontos: 3500,
      nivel: 'Diamante',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20sales%20manager%20confident%20business%20suit%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=roberto7&orientation=squarish',
      atendimentos: 1,
      dataIngresso: new Date('2024-01-01'),
      ultimaAtividade: new Date('2024-01-20'),
      cnpj: '12.345.678/0001-90',
      statusCampanha: 'Ativo',
      publicoAlvo: 'PJ',
      campanhas: [
        {
          id: 1,
          nome: 'Vendas Q4 2024',
          tipo: 'Vendas',
          status: 'Ativa',
          nivelParticipante: 'Diamante',
          pontosAcumulados: 3500,
          metaAtingida: 98,
          posicaoRanking: 1,
          distintivos: [
            { nome: 'Líder', icone: 'ri-vip-crown-line', cor: 'text-purple-500' },
            { nome: 'Top Vendedor', icone: 'ri-trophy-line', cor: 'text-yellow-500' }
          ],
          missoesConcluidas: 10,
          missoesTotais: 10,
          dataInicio: '01/10/2024',
          dataFim: '31/12/2024',
          atendimentos: 1,
          statusParticipacao: 'Ativo',
          convites: { enviados: 6, aceitos: 5 },
          comunicacao: { abertura: 30, resposta: 22 },
          contratos: { assinados: 5, pendentes: 0 }
        },
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Diamante',
          pontosAcumulados: 2950,
          metaAtingida: 92,
          posicaoRanking: 1,
          distintivos: [
            { nome: 'Excelência', icone: 'ri-award-line', cor: 'text-blue-500' }
          ],
          missoesConcluidas: 9,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 1,
          statusParticipacao: 'Ativo',
          convites: { enviados: 4, aceitos: 4 },
          comunicacao: { abertura: 25, resposta: 18 },
          contratos: { assinados: 3, pendentes: 1 }
        }
      ]
    },
    {
      id: '8',
      nome: 'Camila Souza',
      email: 'camila.souza@empresa.com',
      departamento: 'RH',
      cargo: 'Analista de RH',
      pontos: 1750,
      nivel: 'Bronze',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20HR%20analyst%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=camila8&orientation=squarish',
      atendimentos: 15,
      dataIngresso: new Date('2024-01-14'),
      ultimaAtividade: new Date('2024-01-17'),
      cpf: '159.753.486-08',
      statusCampanha: 'Pendente',
      publicoAlvo: 'AUTONOMO',
      campanhas: [
        {
          id: 3,
          nome: 'Captação Novos Clientes',
          tipo: 'Aquisição',
          status: 'Ativa',
          nivelParticipante: 'Bronze',
          pontosAcumulados: 1750,
          metaAtingida: 55,
          posicaoRanking: 6,
          distintivos: [],
          missoesConcluidas: 5,
          missoesTotais: 10,
          dataInicio: '15/11/2024',
          dataFim: '15/01/2025',
          atendimentos: 15,
          statusParticipacao: 'Ativo',
          convites: { enviados: 2, aceitos: 1 },
          comunicacao: { abertura: 9, resposta: 4 },
          contratos: { assinados: 1, pendentes: 1 }
        }
      ]
    },
    {
      id: '9',
      nome: 'Rafael Barbosa',
      email: 'rafael.barbosa@empresa.com',
      departamento: 'Operações',
      cargo: 'Coordenador de Operações',
      pontos: 2650,
      nivel: 'Ouro',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20operations%20coordinator%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=rafael9&orientation=squarish',
      atendimentos: 4,
      dataIngresso: new Date('2024-01-06'),
      ultimaAtividade: new Date('2024-01-19'),
      cpf: '753.159.486-09',
      statusCampanha: 'Pendente',
      publicoAlvo: 'CLT',
      campanhas: [
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Ouro',
          pontosAcumulados: 2650,
          metaAtingida: 78,
          posicaoRanking: 3,
          distintivos: [
            { nome: 'Eficiência', icone: 'ri-flashlight-line', cor: 'text-yellow-500' }
          ],
          missoesConcluidas: 7,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 4,
          statusParticipacao: 'Ativo',
          convites: { enviados: 3, aceitos: 3 },
          comunicacao: { abertura: 16, resposta: 10 },
          contratos: { assinados: 2, pendentes: 1 }
        }
      ]
    },
    {
      id: '10',
      nome: 'Thiago Pereira',
      email: 'thiago.pereira@empresa.com',
      departamento: 'Vendas',
      cargo: 'Consultor de Vendas',
      pontos: 1950,
      nivel: 'Prata',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20sales%20consultant%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=thiago10&orientation=squarish',
      atendimentos: 9,
      dataIngresso: new Date('2024-01-11'),
      ultimaAtividade: new Date('2024-01-18'),
      cpf: '486.159.753-10',
      statusCampanha: 'Pendente',
      publicoAlvo: 'PF',
      campanhas: [
        {
          id: 1,
          nome: 'Vendas Q4 2024',
          tipo: 'Vendas',
          status: 'Ativa',
          nivelParticipante: 'Prata',
          pontosAcumulados: 1950,
          metaAtingida: 65,
          posicaoRanking: 7,
          distintivos: [
            { nome: 'Promissor', icone: 'ri-seedling-line', cor: 'text-green-500' }
          ],
          missoesConcluidas: 6,
          missoesTotais: 10,
          dataInicio: '01/10/2024',
          dataFim: '31/12/2024',
          atendimentos: 9,
          statusParticipacao: 'Ativo',
          convites: { enviados: 2, aceitos: 2 },
          comunicacao: { abertura: 13, resposta: 7 },
          contratos: { assinados: 1, pendentes: 1 }
        }
      ]
    },
    {
      id: '11',
      nome: 'Lucas Martins',
      email: 'lucas.martins@empresa.com',
      departamento: 'TI',
      cargo: 'Analista de Sistemas',
      pontos: 2300,
      nivel: 'Prata',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20systems%20analyst%20confident%20business%20casual%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=lucas11&orientation=squarish',
      atendimentos: 6,
      dataIngresso: new Date('2024-01-09'),
      ultimaAtividade: new Date('2024-01-20'),
      cpf: '369.258.147-11',
      statusCampanha: 'Pendente',
      publicoAlvo: 'MEI',
      campanhas: [
        {
          id: 2,
          nome: 'Meta Dezembro',
          tipo: 'Performance',
          status: 'Ativa',
          nivelParticipante: 'Prata',
          pontosAcumulados: 2300,
          metaAtingida: 72,
          posicaoRanking: 4,
          distintivos: [
            { nome: 'Técnico', icone: 'ri-code-line', cor: 'text-blue-500' }
          ],
          missoesConcluidas: 7,
          missoesTotais: 10,
          dataInicio: '01/12/2024',
          dataFim: '31/12/2024',
          atendimentos: 6,
          statusParticipacao: 'Ativo',
          convites: { enviados: 3, aceitos: 2 },
          comunicacao: { abertura: 14, resposta: 8 },
          contratos: { assinados: 2, pendentes: 1 }
        }
      ]
    },
    {
      id: '12',
      nome: 'Beatriz Almeida',
      email: 'beatriz.almeida@empresa.com',
      departamento: 'Atendimento',
      cargo: 'Supervisora de Atendimento',
      pontos: 2750,
      nivel: 'Ouro',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20customer%20service%20supervisor%20confident%20business%20attire%20modern%20office%20background%20clean%20corporate%20style&width=40&height=40&seq=beatriz12&orientation=squarish',
      atendimentos: 11,
      dataIngresso: new Date('2024-01-04'),
      ultimaAtividade: new Date('2024-01-19'),
      cpf: '258.147.369-12',
      statusCampanha: 'Pendente',
      publicoAlvo: 'CLT',
      campanhas: [
        {
          id: 3,
          nome: 'Captação Novos Clientes',
          tipo: 'Aquisição',
          status: 'Ativa',
          nivelParticipante: 'Ouro',
          pontosAcumulados: 2750,
          metaAtingida: 82,
          posicaoRanking: 3,
          distintivos: [
            { nome: 'Atencioso', icone: 'ri-customer-service-line', cor: 'text-teal-500' }
          ],
          missoesConcluidas: 8,
          missoesTotais: 10,
          dataInicio: '15/11/2024',
          dataFim: '15/01/2025',
          atendimentos: 11,
          statusParticipacao: 'Ativo',
          convites: { enviados: 4, aceitos: 3 },
          comunicacao: { abertura: 19, resposta: 12 },
          contratos: { assinados: 3, pendentes: 0 }
        }
      ]
    }
  ];

  const departamentos = ['Vendas', 'Marketing', 'Atendimento', 'Financeiro', 'RH', 'TI'];

  // Filtrar apenas participantes com status "Pendente" para o modal de convites
  const participantesPendentes = mockParticipantes.filter(p => p.statusCampanha === 'Pendente');

  const filteredParticipantes = mockParticipantes.filter(p => {
    const matchesSearch = p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartamento = filtroDepartamento === 'todos' || p.departamento === filtroDepartamento;
    const matchesStatus = filtroStatus === 'todos' || p.statusCampanha === filtroStatus;
    
    return matchesSearch && matchesDepartamento && matchesStatus;
  });

  // Filtrar participantes pendentes para o modal de convites
  const filteredParticipantesPendentes = participantesPendentes.filter(p => {
    if (tipoConvite === 'selecionados') {
      const matchesSearch = p.nome.toLowerCase().includes(filtroParticipante.toLowerCase()) ||
                           p.email.toLowerCase().includes(filtroParticipante.toLowerCase()) ||
                           (p.cpf && p.cpf.includes(filtroParticipante)) ||
                           (p.cnpj && p.cnpj.includes(filtroParticipante));
      return matchesSearch;
    }
    return true;
  });

  const [filtroParticipante, setFiltroParticipante] = useState('');
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [participanteParaAtendimento, setParticipanteParaAtendimento] = useState<Participante | null>(null);

  const getNivelColor = (nivel: string) => {
    const colors = {
      'Bronze': 'bg-amber-100 text-amber-800',
      'Prata': 'bg-gray-100 text-gray-800',
      'Ouro': 'bg-yellow-100 text-yellow-800',
      'Diamante': 'bg-purple-100 text-purple-800'
    };
    return colors[nivel as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo: string) => {
    const colors = {
      'Vendas': 'bg-blue-100 text-blue-800',
      'Performance': 'bg-orange-100 text-orange-800',
      'Aquisição': 'bg-emerald-100 text-emerald-800',
      'Capacitação': 'bg-slate-100 text-slate-800'
    };
    return colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getAtendimentosColor = (quantidade: number) => {
    if (quantidade >= 10) return 'bg-red-100 text-red-800';
    if (quantidade >= 5) return 'bg-yellow-100 text-yellow-800';
    if (quantidade >= 1) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getAtendimentosLabel = (quantidade: number) => {
    if (quantidade === 0) return 'Nenhum';
    if (quantidade === 1) return '1 atendimento';
    return `${quantidade} atendimentos`;
  };

  const handleEdit = (participanteId: string) => {
    const participante = mockParticipantes.find(p => p.id === participanteId);
    if (participante) {
      setParticipanteSelecionado(participante);
      setShowEditModal(true);
    }
  };

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [participanteParaExcluir, setParticipanteParaExcluir] = useState<{ id: string; nome: string } | null>(null);

  const handleDelete = (participanteId: string) => {
    const participante = mockParticipantes.find(p => p.id === participanteId);
    if (participante) {
      setParticipanteParaExcluir({ id: participante.id, nome: participante.nome });
      setShowDeleteConfirmModal(true);
    }
  };

  const handleConfirmDelete = () => {
    if (participanteParaExcluir) {
      alert(`Participante "${participanteParaExcluir.nome}" excluído com sucesso!`);
    }
    setShowDeleteConfirmModal(false);
    setParticipanteParaExcluir(null);
  };

  const handleViewTickets = (participanteId: string) => {
    const participante = mockParticipantes.find(p => p.id === participanteId);
    if (participante) {
      setParticipanteParaAtendimento(participante);
      setShowTicketModal(true);
    }
  };

  const handleViewCampanhas = (participante: Participante) => {
    setParticipanteSelecionado(participante);
    setShowCampanhasModal(true);
  };

  const handleEnviarConvites = () => {
    navigate('/admin/incentivo/participantes/enviar-convites');
  };

  const handleSelecionarParticipante = (participanteId: string) => {
    if (participantesSelecionados.includes(participanteId)) {
      setParticipantesSelecionados(participantesSelecionados.filter(id => id !== participanteId));
    } else {
      setParticipantesSelecionados([...participantesSelecionados, participanteId]);
    }
  };

  const handleSelecionarTodos = () => {
    if (participantesSelecionados.length === filteredParticipantes.length) {
      setParticipantesSelecionados([]);
    } else {
      setParticipantesSelecionados(filteredParticipantes.map(p => p.id));
    }
  };

  const getParticipantesParaEnvio = () => {
    if (tipoConvite === 'todos') {
      return filteredParticipantesPendentes;
    } else {
      return filteredParticipantesPendentes.filter(p => participantesSelecionados.includes(p.id));
    }
  };

  // Função para obter participantes relacionados às campanhas selecionadas
  const getParticipantesPorCampanhas = () => {
    if (campanhasSelecionadas.length === 0) {
      return participantesPendentes;
    }
    
    return participantesPendentes.filter(p => 
      p.campanhas.some(c => campanhasSelecionadas.includes(c.id))
    );
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'Pendente': 'bg-orange-100 text-orange-800',
      'Ativo': 'bg-green-100 text-green-800',
      'Inativo': 'bg-gray-100 text-gray-800',
      'Suspenso': 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const handleConfirmarEnvioConvites = () => {
    const participantesEnvio = getParticipantesParaEnvio();
    
    if (participantesEnvio.length === 0) {
      alert('Selecione pelo menos um participante para enviar o convite.');
      return;
    }

    if (!conviteData.enviarPorEmail && !conviteData.enviarPorSMS && !conviteData.enviarPorWhatsApp) {
      alert('Selecione pelo menos um canal de envio (Email, SMS ou WhatsApp).');
      return;
    }

    // Validar SMS (máximo 160 caracteres)
    if (conviteData.enviarPorSMS && conviteData.mensagemSMS.length > 160) {
      alert('A mensagem SMS não pode ter mais de 160 caracteres.');
      return;
    }

    // Simular envio de convites
    setShowConvitesModal(false);
    alert(`Convites enviados com sucesso para ${participantesEnvio.length} participante(s)!`);
    
    // Reset selections
    setParticipantesSelecionados([]);
    setTipoConvite('todos');
  };

  const handleCarregarModelo = (modeloId: string) => {
    const modelo = modelosSalvos.find(m => m.id === modeloId);
    if (modelo) {
      setConviteData({
        ...conviteData,
        assunto: modelo.assunto,
        mensagemEmail: modelo.mensagemEmail,
        mensagemSMS: modelo.mensagemSMS
      });
      setModeloSelecionado(modeloId);
    }
  };

  const handleSalvarModelo = () => {
    if (!nomeModelo.trim()) {
      alert('Digite um nome para o modelo.');
      return;
    }

    const novoModelo = {
      id: Date.now().toString(),
      nome: nomeModelo,
      assunto: conviteData.assunto,
      mensagemEmail: conviteData.mensagemEmail,
      mensagemSMS: conviteData.mensagemSMS,
      dataCriacao: new Date()
    };

    setModelosSalvos([...modelosSalvos, novoModelo]);
    setShowSaveTemplateModal(false);
    setNomeModelo('');
    alert('Modelo salvo com sucesso!');
  };

  const handleExcluirModelo = (modeloId: string) => {
    if (confirm('Deseja realmente excluir este modelo?')) {
      setModelosSalvos(modelosSalvos.filter(m => m.id !== modeloId));
      if (modeloSelecionado === modeloId) {
        setModeloSelecionado('');
      }
    }
  };

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteConfig, setInviteConfig] = useState({
    destinatarios: 'todos' as 'todos' | 'selecionados',
    assunto: 'Convite para Participar do Programa de Incentivos',
    mensagem: 'Olá [NOME],\n\nVocê foi convidado(a) a participar do nosso Programa de Incentivos!\n\nAcesse através do link: [LINK_CONVITE]\n\nAproveite todos os benefícios e recompensas disponíveis.',
    canais: {
      email: true,
      sms: false,
      whatsapp: false
    },
    agendamento: 'imediato' as 'imediato' | 'agendado',
    dataAgendamento: '',
    horaAgendamento: ''
  });

  const [selectedParticipantes, setSelectedParticipantes] = useState<string[]>([]);

  const handleSendInvites = () => {
    const participantesSelecionados = inviteConfig.destinatarios === 'todos' 
      ? filteredParticipantes 
      : filteredParticipantes.filter(p => selectedParticipantes.includes(p.id));

    if (participantesSelecionados.length === 0) {
      alert('Selecione pelo menos um participante para enviar convites.');
      return;
    }

    if (!inviteConfig.canais.email && !inviteConfig.canais.sms && !inviteConfig.canais.whatsapp) {
      alert('Selecione pelo menos um canal de envio (Email, SMS ou WhatsApp).');
      return;
    }

    // Simular envio de convites
    console.log('Enviando convites para:', participantesSelecionados);
    console.log('Configuração:', inviteConfig);

    alert(`${participantesSelecionados.length} convite(s) ${inviteConfig.agendamento === 'imediato' ? 'enviado(s)' : 'agendado(s)'} com sucesso!`);
    setShowInviteModal(false);
    setSelectedParticipantes([]);
  };

  const getInviteStats = () => {
    const participantesSelecionados = inviteConfig.destinatarios === 'todos' 
      ? filteredParticipantes 
      : filteredParticipantes.filter(p => selectedParticipantes.includes(p.id));

    return {
      total: participantesSelecionados.length,
      email: inviteConfig.canais.email ? participantesSelecionados.length : 0,
      sms: inviteConfig.canais.sms ? participantesSelecionados.length : 0,
      whatsapp: inviteConfig.canais.whatsapp ? participantesSelecionados.length : 0
    };
  };

  const getPublicoAlvoColor = (publico: string) => {
    const colors = {
      'PF': 'bg-blue-100 text-blue-800',
      'PJ': 'bg-purple-100 text-purple-800',
      'MEI': 'bg-green-100 text-green-800',
      'CLT': 'bg-orange-100 text-orange-800',
      'AUTONOMO': 'bg-teal-100 text-teal-800',
      'REPRESENTANTE': 'bg-indigo-100 text-indigo-800',
      'DISTRIBUIDOR': 'bg-pink-100 text-pink-800'
    };
    return colors[publico as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTipoIcon = (publico: string) => {
    const icons = {
      'PF': 'ri-user-line',
      'PJ': 'ri-building-line',
      'MEI': 'ri-store-line',
      'CLT': 'ri-briefcase-line',
      'AUTONOMO': 'ri-user-star-line',
      'REPRESENTANTE': 'ri-user-voice-line',
      'DISTRIBUIDOR': 'ri-truck-line'
    };
    return icons[publico as keyof typeof icons] || 'ri-user-line';
  };

  const handleViewParticipante = (participante: Participante) => {
    if (expandedParticipante === participante.id) {
      setExpandedParticipante(null);
    } else {
      setExpandedParticipante(participante.id);
    }
  };

  const getTipoPremiacaoIcon = (tipo: string) => {
    const icons = {
      'Gift Card': 'ri-gift-line',
      'Dinheiro': 'ri-money-dollar-circle-line',
      'Wallet': 'ri-wallet-3-line',
      'Cartão Pré-pago': 'ri-bank-card-line',
      'Produto Físico': 'ri-box-3-line'
    };
    return icons[tipo as keyof typeof icons] || 'ri-gift-line';
  };

  const getTipoPremiacaoColor = (tipo: string) => {
    const colors = {
      'Gift Card': 'bg-purple-100 text-purple-800 border-purple-200',
      'Dinheiro': 'bg-green-100 text-green-800 border-green-200',
      'Wallet': 'bg-blue-100 text-blue-800 border-blue-200',
      'Cartão Pré-pago': 'bg-orange-100 text-orange-800 border-orange-200',
      'Produto Físico': 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusPremiacaoColor = (status: string) => {
    const colors = {
      'Processado': 'bg-yellow-100 text-yellow-800',
      'Pendente': 'bg-orange-100 text-orange-800',
      'Entregue': 'bg-green-100 text-green-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusParticipacaoColor = (status: string) => {
    const colors = {
      'Ativo': 'bg-green-100 text-green-800',
      'Pausado': 'bg-yellow-100 text-yellow-800',
      'Concluído': 'bg-blue-100 text-blue-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getGrupoTrabalhoColor = (grupo: string) => {
    const colors: { [key: string]: string } = {
      'Financeiro': 'bg-green-100 text-green-800',
      'Operacional': 'bg-blue-100 text-blue-800',
      'Atendimento': 'bg-purple-100 text-purple-800',
      'Vendas': 'bg-orange-100 text-orange-800',
      'Marketing': 'bg-pink-100 text-pink-800',
      'TI': 'bg-indigo-100 text-indigo-800'
    };
    return colors[grupo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('participants.title')}</h1>
            <p className="text-gray-600 mt-1">{t('participants.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowJourney(!showJourney)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={showJourney ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </div>
              <span>{showJourney ? t('common.hide') : t('common.show')} Jornada</span>
            </button>
            <button
              onClick={() => navigate('/admin/incentivo/participantes/cadastros')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
              <span>{t('participants.buttons.newParticipant')}</span>
            </button>
          </div>
        </div>

        {/* Jornada dos Participantes */}
        {showJourney && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Jornada dos Participantes</h2>
              <p className="text-gray-600">Acompanhe todas as etapas do ciclo de vida dos participantes no programa</p>
            </div>

            {/* Carousel Container */}
            <div className="relative">
              {/* Botão Anterior */}
              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className={`absolute left-0 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white border-2 rounded-full flex items-center justify-center transition-all shadow-lg ${
                  currentSlide === 0 
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                }`}
                aria-label="Card anterior"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>

              {/* Botão Próximo */}
              <button
                onClick={nextSlide}
                disabled={currentSlide === totalSlides - 1}
                className={`absolute right-0 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white border-2 rounded-full flex items-center justify-center transition-all shadow-lg ${
                  currentSlide === totalSlides - 1
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                }`}
                aria-label="Próximo card"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>

              {/* Cards Container com Overflow Hidden */}
              <div className="overflow-hidden px-12">
                <div 
                  className="flex gap-6 transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * (100 / totalSlides + 2.4)}%)` }}
                >
                  {/* Etapa 1: Cadastro */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-emerald-50/50 border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 hover:border-emerald-300 h-full flex flex-col">
                      {/* Step Number and Icon */}
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center relative z-10">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-user-add-line text-white text-xl"></i>
                          </div>
                        </div>
                      </div>

                      {/* Step Info */}
                      <div className="text-center mb-4 flex-shrink-0">
                        <div className="text-xs font-medium text-gray-500 mb-1">ETAPA 1</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Cadastro</h3>
                        <p className="text-sm text-gray-600">Preenchimento de dados pessoais e profissionais</p>
                      </div>

                      {/* Step Details */}
                      <div className="space-y-2 flex-grow">
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Dados pessoais completos</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Informações de contato</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Dados bancários</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Validação de informações</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Upload de documentos</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold text-emerald-600">35</div>
                            <div className="text-xs text-gray-500">Completos</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-orange-600">3</div>
                            <div className="text-xs text-gray-500">Pendentes</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-4">
                        <button 
                          onClick={() => navigate('/admin/incentivo/participantes/cadastros')}
                          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Efetuar Cadastros
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Etapa 2: Performance */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-orange-50/50 border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 hover:border-orange-300 h-full flex flex-col">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center relative z-10">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-line-chart-line text-white text-xl"></i>
                          </div>
                        </div>
                      </div>

                      <div className="text-center mb-4 flex-shrink-0">
                        <div className="text-xs font-medium text-gray-500 mb-1">ETAPA 2</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Performance</h3>
                        <p className="text-sm text-gray-600">Registro de vendas de produtos e serviços</p>
                      </div>

                      <div className="space-y-2 flex-grow">
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Cadastro manual de vendas</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Importação via CSV</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Integração via API</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Produtos e serviços</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Insumo para metas</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold text-orange-600">42</div>
                            <div className="text-xs text-gray-500">Registros</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-emerald-600">38</div>
                            <div className="text-xs text-gray-500">Aprovados</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button 
                          onClick={() => navigate('/admin/incentivo/participantes/performance')}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Gerenciar Performance
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Etapa 3: Convite */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-blue-50/50 border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 hover:border-blue-300 h-full flex flex-col">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center relative z-10">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-mail-send-line text-white text-xl"></i>
                          </div>
                        </div>
                      </div>

                      <div className="text-center mb-4 flex-shrink-0">
                        <div className="text-xs font-medium text-gray-500 mb-1">ETAPA 3</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Convite</h3>
                        <p className="text-sm text-gray-600">Envio de convites para participação no programa</p>
                      </div>

                      <div className="space-y-2 flex-grow">
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Envio de convites por email</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Link de acesso personalizado</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Acompanhamento de aceites</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Reenvio automático</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Controle de pendências</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold text-blue-600">45</div>
                            <div className="text-xs text-gray-500">Enviados</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-emerald-600">38</div>
                            <div className="text-xs text-gray-500">Aceitos</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button 
                          onClick={handleEnviarConvites}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          Enviar Convites
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Etapa 4: Comunicação */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-purple-50/50 border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 hover:border-purple-300 h-full flex flex-col">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center relative z-10">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-message-3-line text-white text-xl"></i>
                          </div>
                        </div>
                      </div>

                      <div className="text-center mb-4 flex-shrink-0">
                        <div className="text-xs font-medium text-gray-500 mb-1">ETAPA 4</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Comunicação</h3>
                        <p className="text-sm text-gray-600">Engajamento e comunicação contínua</p>
                      </div>

                      <div className="space-y-2 flex-grow">
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Notificações de campanhas</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Atualizações de pontos</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Lembretes de metas</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Notificação de Gameficações</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Suporte e atendimento</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold text-purple-600">87%</div>
                            <div className="text-xs text-gray-500">Abertura</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-blue-600">62%</div>
                            <div className="text-xs text-gray-500">Resposta</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button className="w-full bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                          Gerenciar Comunicação
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Etapa 5: Contratos e Regulamentos */}
                  <div className="w-80 flex-shrink-0">
                    <div className="bg-orange-50/50 border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 hover:border-orange-300 h-full flex flex-col">
                      <div className="flex items-center justify-center mb-4">
                        <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center relative z-10">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-file-text-line text-white text-xl"></i>
                          </div>
                        </div>
                      </div>

                      <div className="text-center mb-4 flex-shrink-0">
                        <div className="text-xs font-medium text-gray-500 mb-1">ETAPA 5</div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Contratos e Regulamentos</h3>
                        <p className="text-sm text-gray-600">Aceite de termos e documentação legal</p>
                      </div>

                      <div className="space-y-2 flex-grow">
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Termos de participação</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Regulamento do programa</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">LGPD e privacidade</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Assinatura digital</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 flex-shrink-0"></div>
                          <span className="text-xs text-gray-600">Arquivo de documentos</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="text-xl font-bold text-emerald-600">35</div>
                            <div className="text-xs text-gray-500">Assinados</div>
                          </div>
                          <div>
                            <div className="text-xl font-bold text-orange-600">3</div>
                            <div className="text-xs text-gray-500">Pendentes</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                          Gerenciar Contratos
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Indicadores de Navegação (Dots) */}
              <div className="flex items-center justify-center space-x-2 mt-6">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`transition-all cursor-pointer ${
                      currentSlide === index
                        ? 'w-8 h-2 bg-blue-600 rounded-full'
                        : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                    }`}
                    aria-label={`Ir para etapa ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Summary Box */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-white"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Acompanhamento da Jornada</h3>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p><strong>1. Cadastro:</strong> Valide as informações fornecidas e garanta a completude dos dados</p>
                    <p><strong>2. Performance:</strong> Registre vendas de produtos e serviços para alimentar metas e gamificação</p>
                    <p><strong>3. Convite:</strong> Envie convites personalizados e acompanhe a taxa de aceitação em tempo real</p>
                    <p><strong>4. Comunicação:</strong> Mantenha os participantes engajados com comunicações relevantes e oportunas</p>
                    <p><strong>5. Contratos:</strong> Assegure que todos os documentos legais sejam aceitos e arquivados corretamente</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('participants.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{mockParticipantes.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-group-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('participants.stats.active')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{mockParticipantes.filter(p => p.campanhas.length > 0).length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-megaphone-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Campanhas</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{mockParticipantes.reduce((acc, p) => acc + p.campanhas.length, 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-trophy-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Com Atendimentos</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{mockParticipantes.filter(p => p.atendimentos > 0).length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-500 flex items-center justify-center">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-customer-service-line text-white text-xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder={t('participants.filters.search')}
                />
              </div>

              <select
                value={filtroDepartamento}
                onChange={(e) => setFiltroDepartamento(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="todos">{t('participants.filters.allSegments')}</option>
                {departamentos.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>

              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="todos">Todos os Status</option>
                <option value="Pendente">Pendente</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
                <option value="Suspenso">Suspenso</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {filteredParticipantes.length} de {mockParticipantes.length} participantes
              </span>
            </div>
          </div>
        </div>

        {/* Participantes Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-6 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('participants.table.participant')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Departamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Público-Alvo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campanhas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ATENDIMENTOS (30 dias)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('participants.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredParticipantes.map((participante) => (
                  <>
                    <tr key={participante.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewParticipante(participante)}
                          className="text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
                        >
                          <i className={`ri-arrow-${expandedParticipante === participante.id ? 'down' : 'right'}-s-line text-xl`}></i>
                        </button>
                      </td>
                      <td 
                        className="px-6 py-4 whitespace-nowrap cursor-pointer"
                        onClick={() => handleViewParticipante(participante)}
                      >
                        <div className="flex items-center">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={participante.avatar}
                            alt={participante.nome}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 hover:text-teal-600 transition-colors">
                              {participante.nome}
                            </div>
                            <div className="text-sm text-gray-500">
                              {participante.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{participante.departamento}</div>
                        <div className="text-sm text-gray-500">{participante.cargo}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getPublicoAlvoColor(participante.publicoAlvo)}`}>
                          <i className={`${getTipoIcon(participante.publicoAlvo)}`}></i>
                          {participante.publicoAlvo}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full whitespace-nowrap ${
                          participante.campanhas.length === 0 ? 'bg-gray-100 text-gray-800' :
                          participante.campanhas.length === 1 ? 'bg-blue-100 text-blue-800' :
                          participante.campanhas.length === 2 ? 'bg-purple-100 text-purple-800' :
                          'bg-emerald-100 text-emerald-800'
                        }`}>
                          {participante.campanhas.length === 0 ? 'Nenhuma campanha' :
                           participante.campanhas.length === 1 ? '1 campanha' :
                           `${participante.campanhas.length} campanhas`}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getAtendimentosColor(participante.atendimentos)}`}>
                          {getAtendimentosLabel(participante.atendimentos)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {participante.campanhas.length > 0 && (
                            <button
                              onClick={() => handleViewCampanhas(participante)}
                              className="w-8 h-8 flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-lg transition-colors cursor-pointer"
                              title="Ver Campanhas"
                            >
                              <i className="ri-list-check"></i>
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(participante.id)}
                            className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(participante.id)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                          {participante.atendimentos > 0 && (
                            <button
                              onClick={() => handleViewTickets(participante.id)}
                              className="w-8 h-8 flex items-center justify-center text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                              title="Ver Atendimentos"
                            >
                              <i className="ri-customer-service-2-line"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Linha Expandida com Detalhes */}
                    {expandedParticipante === participante.id && (
                      <tr>
                        <td colSpan={8} className="bg-gradient-to-br from-gray-50 to-gray-100 border-t border-b border-gray-200">
                          <div className="p-6">
                            {/* Stats Rápidos */}
                            <div className="grid grid-cols-4 gap-3 mb-6">
                              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-medium text-gray-600">Total de Pontos</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{participante.pontos.toLocaleString('pt-BR')}</p>
                                  </div>
                                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                                    <i className="ri-coin-line text-white text-lg"></i>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-medium text-gray-600">Campanhas Ativas</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{participante.campanhas.length}</p>
                                  </div>
                                  <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                                    <i className="ri-megaphone-line text-white text-lg"></i>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-medium text-gray-600">Atendimentos (30d)</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{participante.atendimentos}</p>
                                  </div>
                                  <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                                    <i className="ri-customer-service-line text-white text-lg"></i>
                                  </div>
                                </div>
                              </div>

                              <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-medium text-gray-600">Premiações (30d)</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{participante.premiacoes?.length || 0}</p>
                                  </div>
                                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                                    <i className="ri-gift-line text-white text-lg"></i>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Conteúdo Principal em 2 Colunas */}
                            <div className="grid grid-cols-2 gap-6">
                              {/* Coluna Esquerda: Campanhas */}
                              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col h-full">
                                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 flex-shrink-0">
                                  <i className="ri-trophy-line text-teal-600"></i>
                                  Campanhas Participando
                                </h4>

                                {participante.campanhas.length === 0 ? (
                                  <div className="text-center py-6">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                      <i className="ri-megaphone-line text-gray-400 text-xl"></i>
                                    </div>
                                    <p className="text-xs text-gray-600">Nenhuma campanha ativa</p>
                                  </div>
                                ) : (
                                  <div className="space-y-3 overflow-y-auto pr-2 flex-1">
                                    {participante.campanhas.map((campanha) => (
                                      <div key={campanha.id} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex-1">
                                            <h5 className="text-sm font-semibold text-gray-900 mb-1">{campanha.nome}</h5>
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTipoColor(campanha.tipo)}`}>
                                                {campanha.tipo}
                                              </span>
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusParticipacaoColor(campanha.statusParticipacao)}`}>
                                                {campanha.statusParticipacao}
                                              </span>
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getNivelColor(campanha.nivelParticipante)}`}>
                                                {campanha.nivelParticipante}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                          <div className="bg-blue-50 rounded p-2">
                                            <p className="text-xs text-blue-700">Pontos</p>
                                            <p className="text-sm font-bold text-blue-900">{campanha.pontosAcumulados.toLocaleString('pt-BR')}</p>
                                          </div>
                                          <div className="bg-purple-50 rounded p-2">
                                            <p className="text-xs text-purple-700">Ranking</p>
                                            <p className="text-sm font-bold text-purple-900">#{campanha.posicaoRanking}</p>
                                          </div>
                                          <div className="bg-orange-50 rounded p-2">
                                            <p className="text-xs text-orange-700">Atend.</p>
                                            <p className="text-sm font-bold text-orange-900">{campanha.atendimentos}</p>
                                          </div>
                                        </div>

                                        {/* NOVAS MÉTRICAS: Convites, Comunicação e Contratos */}
                                        <div className="flex items-center gap-3 text-xs border-t border-gray-100 pt-3 mt-3">
                                          {/* Convites */}
                                          <div className="flex items-center gap-1.5 text-teal-700">
                                            <i className="ri-mail-send-line text-sm" title="Convites"></i>
                                            <span className="font-medium" title="Total de convites enviados">{campanha.convites.enviados}</span>
                                            <span className="text-teal-600">env.</span>
                                            <i className="ri-checkbox-circle-line text-teal-600" title="Convites aceitos"></i>
                                            <span className="font-medium" title="Total de convites aceitos">{campanha.convites.aceitos}</span>
                                            <span className="text-teal-600">aceit.</span>
                                          </div>

                                          {/* Separador */}
                                          <div className="w-px h-4 bg-gray-300"></div>

                                          {/* Comunicação */}
                                          <div className="flex items-center gap-1.5 text-indigo-700">
                                            <i className="ri-message-3-line text-sm" title="Comunicações"></i>
                                            <span className="font-medium" title="Total de comunicações abertas">{campanha.comunicacao.abertura}</span>
                                            <i className="ri-eye-line text-indigo-600" title="Aberturas"></i>
                                            <span className="text-indigo-600">abert.</span>
                                            <i className="ri-reply-line text-indigo-600" title="Respostas"></i>
                                            <span className="font-medium" title="Total de respostas recebidas">{campanha.comunicacao.resposta}</span>
                                            <span className="text-indigo-600">resp.</span>
                                          </div>

                                          {/* Separador */}
                                          <div className="w-px h-4 bg-gray-300"></div>

                                          {/* Contratos */}
                                          <div className="flex items-center gap-1.5 text-emerald-700">
                                            <i className="ri-file-text-line text-sm" title="Contratos"></i>
                                            <span className="font-medium" title="Total de contratos assinados">{campanha.contratos.assinados}</span>
                                            <i className="ri-checkbox-circle-line text-emerald-600" title="Assinados"></i>
                                            <span className="text-emerald-600">assin.</span>
                                            <i className="ri-time-line text-yellow-600" title="Pendentes"></i>
                                            <span className="font-medium" title="Total de contratos pendentes">{campanha.contratos.pendentes}</span>
                                            <span className="text-yellow-600">pend.</span>
                                          </div>
                                        </div>

                                        <div className="mb-2">
                                          <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-gray-600">Meta</span>
                                            <span className="font-semibold text-gray-900">{campanha.metaAtingida}%</span>
                                          </div>
                                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                                            <div 
                                              className="bg-teal-600 h-1.5 rounded-full transition-all"
                                              style={{ width: `${campanha.metaAtingida}%` }}
                                            ></div>
                                          </div>
                                        </div>

                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-600 flex items-center gap-1">
                                            <i className="ri-task-line"></i>
                                            Missões: {campanha.missoesConcluidas}/{campanha.missoesTotais}
                                          </span>
                                          {campanha.distintivos.length > 0 && (
                                            <div className="flex items-center gap-1">
                                              {campanha.distintivos.slice(0, 3).map((dist, i) => (
                                                <span key={i} className="w-5 h-5 flex items-center justify-center rounded-full bg-yellow-100">
                                                  <i className={`${dist.icone} ${dist.cor} text-xs`}></i>
                                                </span>
                                              ))}
                                              {campanha.distintivos.length > 3 && (
                                                <span className="text-xs text-gray-600">+{campanha.distintivos.length - 3}</span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Coluna Direita: Timeline de Premiações */}
                              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                  <i className="ri-gift-line text-teal-600"></i>
                                  Premiações (30 dias)
                                </h4>

                                {!participante.premiacoes || participante.premiacoes.length === 0 ? (
                                  <div className="text-center py-6">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                      <i className="ri-gift-line text-gray-400 text-xl"></i>
                                    </div>
                                    <p className="text-xs text-gray-600">Nenhuma premiação</p>
                                  </div>
                                ) : (
                                  <>
                                    {/* Resumo por Tipo */}
                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                      {Array.from(new Set(participante.premiacoes.map(p => p.tipo))).slice(0, 4).map((tipo) => {
                                        const total = participante.premiacoes!.filter(p => p.tipo === tipo).reduce((sum, p) => sum + p.valor, 0);
                                        const qtd = participante.premiacoes!.filter(p => p.tipo === tipo).length;
                                        
                                        return (
                                          <div key={tipo} className={`border rounded-lg p-2 ${getTipoPremiacaoColor(tipo)}`}>
                                            <div className="flex items-center gap-1 mb-1">
                                              <i className={`${getTipoPremiacaoIcon(tipo)} text-xs`}></i>
                                              <span className="text-xs font-semibold truncate">{tipo}</span>
                                            </div>
                                            <p className="text-sm font-bold">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                            <p className="text-xs opacity-75">{qtd}x</p>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {/* Timeline Compacta */}
                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                                      {participante.premiacoes.slice(0, 8).map((premiacao) => (
                                        <div key={premiacao.id} className="flex gap-2 bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all">
                                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            premiacao.tipo === 'Gift Card' ? 'bg-purple-100 text-purple-600' :
                                            premiacao.tipo === 'Dinheiro' ? 'bg-green-100 text-green-600' :
                                            premiacao.tipo === 'Wallet' ? 'bg-blue-100 text-blue-600' :
                                            premiacao.tipo === 'Cartão Pré-pago' ? 'bg-orange-100 text-orange-600' :
                                            'bg-pink-100 text-pink-600'
                                          }`}>
                                            <i className={`${getTipoPremiacaoIcon(premiacao.tipo)} text-sm`}></i>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-gray-900 truncate">{premiacao.descricao}</p>
                                                <p className="text-xs text-gray-500 truncate">{premiacao.campanha}</p>
                                              </div>
                                              <div className="text-right flex-shrink-0">
                                                <p className="text-sm font-bold text-gray-900">R$ {premiacao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                              </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-1">
                                              <span className="text-xs text-gray-400">{premiacao.data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusPremiacaoColor(premiacao.status)}`}>
                                                {premiacao.status}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Total */}
                                    <div className="mt-3 pt-3 border-t border-gray-200 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-3">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <p className="text-xs font-medium text-teal-800">Total Geral</p>
                                          <p className="text-xs text-teal-600">{participante.premiacoes.length} premiações</p>
                                        </div>
                                        <p className="text-2xl font-bold text-teal-700">
                                          R$ {participante.premiacoes.reduce((sum, p) => sum + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </p>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {filteredParticipantes.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-group-line text-gray-400 text-2xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common.noData')}</h3>
              <p className="text-gray-600 mb-4">Tente ajustar os filtros ou adicione novos participantes</p>
            </div>
          )}
        </div>

        {/* Modal de Adicionar Participante */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Cadastrar Novo Participante</h3>
                  <p className="text-sm text-gray-600 mt-1">Preencha todas as informações necessárias para o cadastro</p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="p-6">
                <form className="space-y-8">
                  {/* Tipo de Participante */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Tipo de Participante</h4>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="tipoParticipante"
                          value="fisica"
                          checked={tipoParticipante === 'fisica'}
                          onChange={(e) => setTipoParticipante(e.target.value as 'fisica' | 'juridica')}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Pessoa Física</span>
                      </label>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="tipoParticipante"
                          value="juridica"
                          checked={tipoParticipante === 'juridica'}
                          onChange={(e) => setTipoParticipante(e.target.value as 'fisica' | 'juridica')}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Pessoa Jurídica</span>
                      </label>
                    </div>
                  </div>

                  {/* Dados Básicos */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados Básicos</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {tipoParticipante === 'fisica' ? 'Nome Completo' : 'Razão Social'} *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.nome}
                          onChange={(e) => setFormData({...formData, nome: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder={tipoParticipante === 'fisica' ? 'Digite o nome completo' : 'Digite a razão social'}
                        />
                      </div>
                      {tipoParticipante === 'juridica' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nome Fantasia</label>
                          <input
                            type="text"
                            value={formData.nomeFantasia}
                            onChange={(e) => setFormData({...formData, nomeFantasia: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite o nome fantasia"
                          />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Digite o email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
                        <input
                          type="tel"
                          value={formData.telefone}
                          onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="(11) 3333-4444"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Celular *</label>
                        <input
                          type="tel"
                          required
                          value={formData.celular}
                          onChange={(e) => setFormData({...formData, celular: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="(11) 99999-8888"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Documentos */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Documentos</h4>
                    {tipoParticipante === 'fisica' ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">CPF *</label>
                          <input
                            type="text"
                            required
                            value={formData.cpf}
                            onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="000.000.000-00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">RG</label>
                          <input
                            type="text"
                            value={formData.rg}
                            onChange={(e) => setFormData({...formData, rg: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="00.000.000-0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Data de Nascimento</label>
                          <input
                            type="date"
                            value={formData.dataNascimento}
                            onChange={(e) => setFormData({...formData, dataNascimento: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Estado Civil</label>
                          <select
                            value={formData.estadoCivil}
                            onChange={(e) => setFormData({...formData, estadoCivil: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                          >
                            <option value="">Selecione</option>
                            <option value="solteiro">Solteiro(a)</option>
                            <option value="casado">Casado(a)</option>
                            <option value="divorciado">Divorciado(a)</option>
                            <option value="viuvo">Viúvo(a)</option>
                            <option value="uniao_estavel">União Estável</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Profissão</label>
                          <input
                            type="text"
                            value={formData.profissao}
                            onChange={(e) => setFormData({...formData, profissao: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite a profissão"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">CNPJ *</label>
                          <input
                            type="text"
                            required
                            value={formData.cnpj}
                            onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="00.000.000/0000-00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Inscrição Estadual</label>
                          <input
                            type="text"
                            value={formData.inscricaoEstadual}
                            onChange={(e) => setFormData({...formData, inscricaoEstadual: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="000.000.000.000"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Porte da Empresa</label>
                          <select
                            value={formData.porte}
                            onChange={(e) => setFormData({...formData, porte: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                          >
                            <option value="">Selecione</option>
                            <option value="mei">MEI</option>
                            <option value="micro">Microempresa</option>
                            <option value="pequena">Pequena Empresa</option>
                            <option value="media">Média Empresa</option>
                            <option value="grande">Grande Empresa</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ramo de Atividade</label>
                          <input
                            type="text"
                            value={formData.ramoAtividade}
                            onChange={(e) => setFormData({...formData, ramoAtividade: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Digite o ramo de atividade"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Endereço */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CEP *</label>
                        <input
                          type="text"
                          required
                          value={formData.cep}
                          onChange={(e) => setFormData({...formData, cep: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="00000-000"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Endereço *</label>
                        <input
                          type="text"
                          required
                          value={formData.endereco}
                          onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Digite o endereço"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Número *</label>
                        <input
                          type="text"
                          required
                          value={formData.numero}
                          onChange={(e) => setFormData({...formData, numero: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="123"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                        <input
                          type="text"
                          value={formData.complemento}
                          onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Apto, Sala, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bairro *</label>
                        <input
                          type="text"
                          required
                          value={formData.bairro}
                          onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Digite o bairro"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cidade *</label>
                        <input
                          type="text"
                          required
                          value={formData.cidade}
                          onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Digite a cidade"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Estado *</label>
                        <select
                          required
                          value={formData.estado}
                          onChange={(e) => setFormData({...formData, estado: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="AC">Acre</option>
                          <option value="AL">Alagoas</option>
                          <option value="AP">Amapá</option>
                          <option value="AM">Amazonas</option>
                          <option value="BA">Bahia</option>
                          <option value="CE">Ceará</option>
                          <option value="DF">Distrito Federal</option>
                          <option value="ES">Espírito Santo</option>
                          <option value="GO">Goiás</option>
                          <option value="MA">Maranhão</option>
                          <option value="MT">Mato Grosso</option>
                          <option value="MS">Mato Grosso do Sul</option>
                          <option value="MG">Minas Gerais</option>
                          <option value="PA">Pará</option>
                          <option value="PB">Paraíba</option>
                          <option value="PR">Paraná</option>
                          <option value="PE">Pernambuco</option>
                          <option value="PI">Piauí</option>
                          <option value="RJ">Rio de Janeiro</option>
                          <option value="RN">Rio Grande do Norte</option>
                          <option value="RS">Rio Grande do Sul</option>
                          <option value="RO">Rondônia</option>
                          <option value="RR">Roraima</option>
                          <option value="SC">Santa Catarina</option>
                          <option value="SP">São Paulo</option>
                          <option value="SE">Sergipe</option>
                          <option value="TO">Tocantins</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Dados Bancários */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados Bancários</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Banco</label>
                        <input
                          type="text"
                          value={formData.banco}
                          onChange={(e) => setFormData({...formData, banco: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Nome do banco"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Agência</label>
                        <input
                          type="text"
                          value={formData.agencia}
                          onChange={(e) => setFormData({...formData, agencia: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="0000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Conta</label>
                        <input
                          type="text"
                          value={formData.conta}
                          onChange={(e) => setFormData({...formData, conta: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="00000-0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Conta</label>
                        <select
                          value={formData.tipoConta}
                          onChange={(e) => setFormData({...formData, tipoConta: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          <option value="">Selecione</option>
                          <option value="corrente">Conta Corrente</option>
                          <option value="poupanca">Conta Poupança</option>
                          <option value="salario">Conta Salário</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Chave PIX</label>
                        <input
                          type="text"
                          value={formData.chavePix}
                          onChange={(e) => setFormData({...formData, chavePix: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="CPF, CNPJ, email, telefone ou chave aleatória"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dados Profissionais */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados Profissionais</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Departamento *</label>
                        <select
                          required
                          value={formData.departamento}
                          onChange={(e) => setFormData({...formData, departamento: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          <option value="">Selecione</option>
                          {departamentos.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cargo *</label>
                        <input
                          type="text"
                          required
                          value={formData.cargo}
                          onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Digite o cargo"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Data de Admissão</label>
                        <input
                          type="date"
                          value={formData.dataAdmissao}
                          onChange={(e) => setFormData({...formData, dataAdmissao: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Salário</label>
                        <input
                          type="number"
                          value={formData.salario}
                          onChange={(e) => setFormData({...formData, salario: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="0,00"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Supervisor/Gestor</label>
                        <input
                          type="text"
                          value={formData.supervisor}
                          onChange={(e) => setFormData({...formData, supervisor: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Nome do supervisor"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Informações do Programa */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Informações do Programa</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nível Inicial</label>
                        <select
                          value={formData.nivelInicial}
                          onChange={(e) => setFormData({...formData, nivelInicial: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          <option value="Bronze">Bronze</option>
                          <option value="Prata">Prata</option>
                          <option value="Ouro">Ouro</option>
                          <option value="Diamante">Diamante</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pontos Iniciais</label>
                        <input
                          type="number"
                          value={formData.pontosIniciais}
                          onChange={(e) => setFormData({...formData, pontosIniciais: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Meta Individual</label>
                        <input
                          type="number"
                          value={formData.metaIndividual}
                          onChange={(e) => setFormData({...formData, metaIndividual: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="Meta mensal"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Observações</label>
                      <textarea
                        value={formData.observacoes}
                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Informações adicionais sobre o participante..."
                      />
                    </div>
                  </div>

                  {/* Configurações */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Configurações</h4>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Receber Notificações</label>
                          <p className="text-xs text-gray-500">Notificações sobre campanhas e pontuações</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.receberNotificacoes}
                            onChange={(e) => setFormData({...formData, receberNotificacoes: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Receber Emails</label>
                          <p className="text-xs text-gray-500">Comunicações por email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.receberEmail}
                            onChange={(e) => setFormData({...formData, receberEmail: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Receber SMS</label>
                          <p className="text-xs text-gray-500">Notificações por SMS</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.receberSMS}
                            onChange={(e) => setFormData({...formData, receberSMS: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Participar do Ranking</label>
                          <p className="text-xs text-gray-500">Aparecer nos rankings públicos</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.participarRanking}
                            onChange={(e) => setFormData({...formData, participarRanking: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Upload de Documentos */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Upload de Documentos</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-upload-cloud-line text-gray-400 text-xl"></i>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Arraste e solte os documentos aqui ou clique para selecionar</p>
                      <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG (máx. 5MB cada)</p>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        id="documentos"
                        onChange={(e) => {
                          if (e.target.files) {
                            setFormData({...formData, documentos: Array.from(e.target.files)});
                          }
                        }}
                      />
                      <label
                        htmlFor="documentos"
                        className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-4 h-4 flex items-center justify-center mr-2">
                          <i className="ri-folder-open-line"></i>
                        </div>
                        Selecionar Arquivos
                      </label>
                    </div>
                    {formData.documentos.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Documentos Selecionados:</h5>
                        <div className="space-y-2">
                          {formData.documentos.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                              <span className="text-sm text-gray-700">{doc.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newDocs = formData.documentos.filter((_, i) => i !== index);
                                  setFormData({...formData, documentos: newDocs});
                                }}
                                className="text-red-600 hover:text-red-800 cursor-pointer"
                              >
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-delete-bin-line"></i>
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowAddModal(false);
                        alert('Participante cadastrado com sucesso!');
                      }}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cadastrar Participante
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Edição do Participante */}
        {showEditModal && participanteSelecionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <img
                    src={participanteSelecionado.avatar}
                    alt={participanteSelecionado.nome}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Editar Participante</h3>
                    <p className="text-sm text-gray-600">{participanteSelecionado.nome}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="p-6">
                <form className="space-y-6">
                  {/* Dados Pessoais */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados Pessoais</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                        <input
                          type="text"
                          defaultValue={participanteSelecionado.nome}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          defaultValue={participanteSelecionado.email}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dados Profissionais */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados Profissionais</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Departamento</label>
                        <select
                          defaultValue={participanteSelecionado.departamento}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          {departamentos.map(dept => (
                            <option key={dept} value={dept}>{dept}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
                        <input
                          type="text"
                          defaultValue={participanteSelecionado.cargo}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Informações do Programa */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Informações do Programa</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pontos Atuais</label>
                        <input
                          type="number"
                          defaultValue={participanteSelecionado.pontos}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Nível</label>
                        <select
                          defaultValue={participanteSelecionado.nivel}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                        >
                          <option value="Bronze">Bronze</option>
                          <option value="Prata">Prata</option>
                          <option value="Ouro">Ouro</option>
                          <option value="Diamante">Diamante</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Data de Ingresso</label>
                        <input
                          type="date"
                          defaultValue={participanteSelecionado.dataIngresso.toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowEditModal(false);
                        alert('Participante atualizado com sucesso!');
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Campanhas do Participante */}
        {showCampanhasModal && participanteSelecionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <img
                    src={participanteSelecionado.avatar}
                    alt={participanteSelecionado.nome}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Campanhas de {participanteSelecionado.nome}</h3>
                    <p className="text-sm text-gray-600">{participanteSelecionado.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCampanhasModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="p-6">
                {participanteSelecionado.campanhas.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-megaphone-line text-gray-400 text-2xl"></i>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma campanha ativa</h3>
                    <p className="text-gray-600">Este participante não está inscrito em nenhuma campanha no momento</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {participanteSelecionado.campanhas.map((campanha) => (
                      <div key={campanha.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        {/* Cabeçalho da Campanha */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-semibold text-gray-900">{campanha.nome}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(campanha.tipo)}`}>
                              {campanha.tipo}
                            </span>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {campanha.status}
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getNivelColor(campanha.nivelParticipante)}`}>
                            Nível {campanha.nivelParticipante}
                          </span>
                        </div>

                        {/* Informações Principais */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-blue-700">Pontos Acumulados</p>
                                <p className="text-lg font-bold text-blue-900 mt-1">{campanha.pontosAcumulados.toLocaleString('pt-BR')}</p>
                              </div>
                              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-coin-line text-white"></i>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-emerald-700">Meta Atingida</p>
                                <p className="text-lg font-bold text-emerald-900 mt-1">{campanha.metaAtingida}%</p>
                              </div>
                              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-target-line text-white"></i>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-purple-700">Posição no Ranking</p>
                                <p className="text-lg font-bold text-purple-900 mt-1">#{campanha.posicaoRanking}</p>
                              </div>
                              <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-trophy-line text-white"></i>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-orange-700">Missões</p>
                                <p className="text-lg font-bold text-orange-900 mt-1">{campanha.missoesConcluidas}/{campanha.missoesTotais}</p>
                              </div>
                              <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                                <div className="w-4 h-4 flex items-center justify-center">
                                  <i className="ri-task-line text-white"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Barra de Progresso */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Progresso da Meta</span>
                            <span className="text-sm font-bold text-gray-900">{campanha.metaAtingida}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                              style={{ width: `${campanha.metaAtingida}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Distintivos */}
                        {campanha.distintivos.length > 0 && (
                          <div className="mb-4">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Distintivos Conquistados</h5>
                            <div className="flex flex-wrap gap-2">
                              {campanha.distintivos.map((distintivo, index) => (
                                <div key={index} className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center space-x-2">
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <i className={`${distintivo.icone} ${distintivo.cor}`}></i>
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">{distintivo.nome}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Período */}
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-calendar-line"></i>
                            </div>
                            <span>Início: {campanha.dataInicio}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-calendar-check-line"></i>
                            </div>
                            <span>Fim: {campanha.dataFim}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Enviar Convites */}
        {showConvitesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Enviar Convites</h3>
                  <p className="text-sm text-gray-600 mt-1">Convide participantes para o programa de incentivo</p>
                </div>
                <button
                  onClick={() => setShowConvitesModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-6">
                  {/* Estatísticas de Envio - NO TOPO */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-700">Total de Convites</p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">{getParticipantesParaEnvio().length}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-mail-send-line text-white"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-700">Por Email</p>
                          <p className="text-2xl font-bold text-emerald-900 mt-1">{conviteData.enviarPorEmail ? getParticipantesParaEnvio().length : 0}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-mail-line text-white"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-purple-700">Por SMS</p>
                          <p className="text-2xl font-bold text-purple-900 mt-1">{conviteData.enviarPorSMS ? getParticipantesParaEnvio().length : 0}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-message-3-line text-white"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-700">Por WhatsApp</p>
                          <p className="text-2xl font-bold text-green-900 mt-1">{conviteData.enviarPorWhatsApp ? getParticipantesParaEnvio().length : 0}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-whatsapp-line text-white"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Seleção de Campanhas - MOVIDO PARA PRIMEIRO */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Campanhas</h4>
                    <p className="text-sm text-gray-600 mb-4">Selecione as campanhas para as quais os participantes serão convidados</p>
                    
                    {/* Seleção de Todas as Campanhas */}
                    <div className="mb-3">
                      <label className="flex items-center space-x-3 cursor-pointer p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                          type="checkbox"
                          checked={campanhasSelecionadas.length === campanhasDisponiveis.length}
                          onChange={handleSelecionarTodasCampanhas}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Selecionar todas as campanhas</span>
                      </label>
                    </div>

                    {/* Lista de Campanhas */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {campanhasDisponiveis.map((campanha) => (
                        <label
                          key={campanha.id}
                          className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            campanhasSelecionadas.includes(campanha.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={campanhasSelecionadas.includes(campanha.id)}
                            onChange={() => handleSelecionarCampanha(campanha.id)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-sm font-semibold text-gray-900">{campanha.nome}</h5>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                campanha.status === 'Ativa' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {campanha.status}
                              </span>
                            </div>
                            <div className="flex items-center space-x-3 text-xs text-gray-600">
                              <span className={`px-2 py-0.5 rounded ${getTipoColor(campanha.tipo)}`}>
                                {campanha.tipo}
                              </span>
                              <span className="flex items-center space-x-1">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-group-line"></i>
                                </div>
                                <span>{campanha.participantes} participantes</span>
                              </span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Resumo das Campanhas Selecionadas */}
                    {campanhasSelecionadas.length > 0 && (
                      <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-checkbox-circle-line text-emerald-600"></i>
                          </div>
                          <span className="text-sm font-medium text-emerald-900">Campanhas Selecionadas</span>
                        </div>
                        <p className="text-sm text-emerald-800">
                          {campanhasSelecionadas.length === campanhasDisponiveis.length
                            ? `Todas as ${campanhasDisponiveis.length} campanhas foram selecionadas.`
                            : `${campanhasSelecionadas.length} campanha(s) selecionada(s).`
                          }
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {campanhasSelecionadas.map(id => {
                            const campanha = campanhasDisponiveis.find(c => c.id === id);
                            return campanha ? (
                              <span key={id} className="inline-flex items-center space-x-1 px-2 py-1 bg-white border border-emerald-300 rounded text-xs font-medium text-emerald-900">
                                <span>{campanha.nome}</span>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleSelecionarCampanha(id);
                                  }}
                                  className="w-3 h-3 flex items-center justify-center text-emerald-600 hover:text-emerald-800 cursor-pointer"
                                >
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Aviso se nenhuma campanha foi selecionada */}
                    {campanhasSelecionadas.length === 0 && (
                      <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-alert-line text-orange-600"></i>
                          </div>
                          <p className="text-sm text-orange-800">
                            Nenhuma campanha selecionada. Os participantes receberão apenas o convite geral para o programa.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Seleção de Destinatários - MOVIDO PARA SEGUNDO */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Destinatários</h4>
                    <p className="text-sm text-gray-600 mb-4">Apenas participantes com status "Pendente" podem receber convites</p>
                    
                    {/* Tipo de Envio */}
                    <div className="mb-4">
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="tipoConvite"
                            value="todos"
                            checked={tipoConvite === 'todos'}
                            onChange={(e) => setTipoConvite(e.target.value as 'todos' | 'selecionados')}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Todos os participantes pendentes ({participantesPendentes.length})</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="tipoConvite"
                            value="selecionados"
                            checked={tipoConvite === 'selecionados'}
                            onChange={(e) => setTipoConvite(e.target.value as 'todos' | 'selecionados')}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Participantes selecionados ({participantesSelecionados.length})</span>
                        </label>
                      </div>
                    </div>

                    {/* Filtro de Busca para Participantes Selecionados */}
                    {tipoConvite === 'selecionados' && (
                      <div className="mb-4">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-search-line text-gray-400"></i>
                            </div>
                          </div>
                          <input
                            type="text"
                            value={filtroParticipante}
                            onChange={(e) => setFiltroParticipante(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Buscar por nome, email, CPF ou CNPJ..."
                          />
                        </div>
                      </div>
                    )}

                    {/* Lista de Participantes para Seleção */}
                    {tipoConvite === 'selecionados' && (
                      <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                        <div className="p-3 border-b border-gray-200 bg-gray-50">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={participantesSelecionados.length === filteredParticipantesPendentes.length}
                              onChange={() => {
                                if (participantesSelecionados.length === filteredParticipantesPendentes.length) {
                                  setParticipantesSelecionados([]);
                                } else {
                                  setParticipantesSelecionados(filteredParticipantesPendentes.map(p => p.id));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Selecionar todos ({filteredParticipantesPendentes.length})</span>
                          </label>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {filteredParticipantesPendentes.map((participante) => (
                            <div key={participante.id} className="p-3 hover:bg-gray-50">
                              <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={participantesSelecionados.includes(participante.id)}
                                  onChange={() => handleSelecionarParticipante(participante.id)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <img
                                  src={participante.avatar}
                                  alt={participante.nome}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">{participante.nome}</div>
                                  <div className="text-xs text-gray-500">
                                    {participante.email} • {participante.departamento}
                                    {participante.cpf && ` • CPF: ${participante.cpf}`}
                                    {participante.cnpj && ` • CNPJ: ${participante.cnpj}`}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(participante.statusCampanha)}`}>
                                  {participante.statusCampanha}
                                </span>
                              </label>
                            </div>
                          ))}
                        </div>
                        {filteredParticipantesPendentes.length === 0 && (
                          <div className="p-4 text-center text-sm text-gray-500">
                            Nenhum participante encontrado com os filtros aplicados
                          </div>
                        )}
                      </div>
                    )}

                    {/* Preview dos Destinatários */}
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-information-line text-blue-600"></i>
                        </div>
                        <span className="text-sm font-medium text-blue-900">Resumo do Envio</span>
                      </div>
                      <p className="text-sm text-blue-800">
                        {tipoConvite === 'todos' 
                          ? `Convites serão enviados para todos os ${participantesPendentes.length} participantes com status "Pendente".`
                          : `Convites serão enviados para ${participantesSelecionados.length} participante(s) selecionado(s) com status "Pendente".`
                        }
                      </p>
                      {campanhasSelecionadas.length > 0 && (
                        <p className="text-sm text-blue-800 mt-1">
                          Participantes serão relacionados às {campanhasSelecionadas.length} campanha(s) selecionada(s).
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Modelos Salvos */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900">Modelos de Convite</h4>
                      <button
                        onClick={() => setShowSaveTemplateModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1 cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-save-line"></i>
                        </div>
                        <span>Salvar como Modelo</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {modelosSalvos.map((modelo) => (
                        <div
                          key={modelo.id}
                          className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                            modeloSelecionado === modelo.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1" onClick={() => handleCarregarModelo(modelo.id)}>
                              <h5 className="text-sm font-semibold text-gray-900">{modelo.nome}</h5>
                              <p className="text-xs text-gray-500 mt-1">
                                Criado em {modelo.dataCriacao.toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExcluirModelo(modelo.id);
                              }}
                              className="text-red-600 hover:text-red-800 cursor-pointer"
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-delete-bin-line text-sm"></i>
                              </div>
                            </button>
                          </div>
                          <div className="text-xs text-gray-600 line-clamp-2">
                            {modelo.assunto}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Canais de Envio */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Canais de Envio</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
                        <input
                          type="checkbox"
                          checked={conviteData.enviarPorEmail}
                          onChange={(e) => setConviteData({...conviteData, enviarPorEmail: e.target.checked})}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-mail-line text-blue-600 text-xl"></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">Email</div>
                          <div className="text-xs text-gray-500">Texto editável</div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-purple-300 transition-colors">
                        <input
                          type="checkbox"
                          checked={conviteData.enviarPorSMS}
                          onChange={(e) => setConviteData({...conviteData, enviarPorSMS: e.target.checked})}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-message-3-line text-purple-600 text-xl"></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">SMS</div>
                          <div className="text-xs text-gray-500">Máx. 160 caracteres</div>
                        </div>
                      </label>

                      <label className="flex items-center space-x-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-green-300 transition-colors">
                        <input
                          type="checkbox"
                          checked={conviteData.enviarPorWhatsApp || false}
                          onChange={(e) => setConviteData({...conviteData, enviarPorWhatsApp: e.target.checked})}
                          className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-whatsapp-line text-green-600 text-xl"></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">WhatsApp</div>
                          <div className="text-xs text-gray-500">Mensagem padrão</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Configuração do Convite - Email */}
                  {conviteData.enviarPorEmail && (
                    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-mail-line text-blue-600"></i>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">Configuração do Email</h4>
                      </div>
                      
                      {/* Assunto */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Assunto</label>
                        <input
                          type="text"
                          value={conviteData.assunto}
                          onChange={(e) => setConviteData({...conviteData, assunto: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                          placeholder="Digite o assunto do email"
                        />
                      </div>

                      {/* Mensagem */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem do Convite</label>
                        <textarea
                          value={conviteData.mensagemEmail}
                          onChange={(e) => setConviteData({...conviteData, mensagemEmail: e.target.value})}
                          rows={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                          placeholder="Digite a mensagem do convite"
                        />
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Variáveis disponíveis:</strong> Use [NOME] para personalizar com o nome do participante e [LINK_CONVITE] para incluir o link de acesso.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Configuração do Convite - SMS */}
                  {conviteData.enviarPorSMS && (
                    <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-message-3-line text-purple-600"></i>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">Configuração do SMS</h4>
                      </div>
                      
                      {/* Mensagem SMS */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Mensagem SMS</label>
                          <span className={`text-xs font-medium ${
                            conviteData.mensagemSMS.length > 160 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {conviteData.mensagemSMS.length}/160 caracteres
                          </span>
                        </div>
                        <textarea
                          value={conviteData.mensagemSMS}
                          onChange={(e) => {
                            if (e.target.value.length <= 160) {
                              setConviteData({...conviteData, mensagemSMS: e.target.value});
                            }
                          }}
                          rows={4}
                          maxLength={160}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
                          placeholder="Digite a mensagem SMS (máximo 160 caracteres)"
                        />
                        <p className="text-xs text-gray-600 mt-2">
                          <strong>Variáveis disponíveis:</strong> [NOME] e [LINK_CONVITE]
                        </p>
                        {conviteData.mensagemSMS.length > 160 && (
                          <p className="text-xs text-red-600 mt-1 flex items-center space-x-1">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-error-warning-line"></i>
                            </div>
                            <span>A mensagem SMS não pode ter mais de 160 caracteres</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Configuração do Convite - WhatsApp */}
                  {conviteData.enviarPorWhatsApp && (
                    <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-whatsapp-line text-green-600"></i>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">Configuração do WhatsApp</h4>
                      </div>
                      
                      <div className="bg-white border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-gray-700 mb-3">
                          <strong>Mensagem Padrão do WhatsApp:</strong>
                        </p>
                        <div className="text-sm text-gray-900 whitespace-pre-line bg-gray-50 p-3 rounded border border-gray-200">
                          Olá [NOME]! 👋

Você foi convidado(a) para participar do nosso programa de incentivo! 🎉

Acesse o link para começar:
[LINK_CONVITE]

Qualquer dúvida, estamos à disposição!
                        </div>
                        <p className="text-xs text-gray-600 mt-3">
                          <strong>Nota:</strong> A mensagem do WhatsApp não pode ser editada e segue um formato padrão otimizado para este canal.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Agendamento */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Agendamento</h4>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={conviteData.agendarEnvio}
                          onChange={(e) => setConviteData({...conviteData, agendarEnvio: e.target.checked})}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-calendar-schedule-line text-gray-600"></i>
                        </div>
                        <span className="text-sm text-gray-700">Agendar Envio</span>
                      </label>
                      
                      {conviteData.agendarEnvio && (
                        <div className="ml-8 grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
                            <input
                              type="date"
                              value={conviteData.dataAgendamento}
                              onChange={(e) => setConviteData({...conviteData, dataAgendamento: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Horário</label>
                            <input
                              type="time"
                              value={conviteData.horaAgendamento}
                              onChange={(e) => setConviteData({...conviteData, horaAgendamento: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview do Convite */}
                  {conviteData.enviarPorEmail && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Preview do Email</h4>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-700">Assunto:</div>
                          <div className="text-sm text-gray-900 mt-1">{conviteData.assunto}</div>
                        </div>
                        <div className="border-t border-gray-200 pt-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">Mensagem:</div>
                          <div className="text-sm text-gray-900 whitespace-pre-line bg-white p-3 rounded border">
                            {conviteData.mensagemEmail
                              .replace('[NOME]', 'João Silva')
                              .replace('[LINK_CONVITE]', 'https://programa.empresa.com/convite/abc123')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botões de Ação */}
                  <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowConvitesModal(false)}
                      className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmarEnvioConvites}
                      disabled={!conviteData.enviarPorEmail && !conviteData.enviarPorSMS && !conviteData.enviarPorWhatsApp}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {conviteData.agendarEnvio ? 'Agendar Envio' : 'Enviar Convites'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Salvar Modelo */}
        {showSaveTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Salvar Modelo</h3>
                <button
                  onClick={() => setShowSaveTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Modelo</label>
                  <input
                    type="text"
                    value={nomeModelo}
                    onChange={(e) => setNomeModelo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Ex: Convite Padrão, Convite Urgente..."
                    autoFocus
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-800">
                    Este modelo salvará o assunto e as mensagens configuradas para Email e SMS.
                  </p>
                </div>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowSaveTemplateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalvarModelo}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Salvar Modelo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Detalhes do Participante */}
        {showDetalhesModal && participanteSelecionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center space-x-4">
                  <img
                    src={participanteSelecionado.avatar}
                    alt={participanteSelecionado.nome}
                    className="w-16 h-16 rounded-full object-cover border-4 border-blue-100"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{participanteSelecionado.nome}</h3>
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-sm text-gray-600">{participanteSelecionado.email}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(participanteSelecionado.nivel)}`}>
                        {participanteSelecionado.nivel}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPublicoAlvoColor(participanteSelecionado.publicoAlvo)}`}>
                        {participanteSelecionado.publicoAlvo}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetalhesModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-2xl"></i>
                  </div>
                </button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4 p-6 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Total de Pontos</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{participanteSelecionado.pontos.toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-coin-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Campanhas Ativas</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{participanteSelecionado.campanhas.length}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-megaphone-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Atendimentos (30d)</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{participanteSelecionado.atendimentos}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-customer-service-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Premiações (30d)</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{participanteSelecionado.premiacoes?.length || 0}</p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-gift-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Campanhas que Participa */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-megaphone-line text-purple-600"></i>
                        </div>
                        <span>Campanhas Ativas</span>
                      </h4>
                      <span className="text-sm font-medium text-gray-600">{participanteSelecionado.campanhas.length} campanhas</span>
                    </div>

                    {participanteSelecionado.campanhas.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-megaphone-line text-gray-400"></i>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">Nenhuma campanha ativa</p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {participanteSelecionado.campanhas.map((campanha) => (
                          <div key={campanha.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900 mb-1">{campanha.nome}</h5>
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTipoColor(campanha.tipo)}`}>
                                    {campanha.tipo}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusParticipacaoColor(campanha.statusParticipacao)}`}>
                                    {campanha.statusParticipacao}
                                  </span>
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(campanha.nivelParticipante)}`}>
                                {campanha.nivelParticipante}
                              </span>
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-3">
                              <div className="bg-blue-50 rounded-lg p-2">
                                <p className="text-xs text-blue-700 font-medium">Pontos</p>
                                <p className="text-sm font-bold text-blue-900">{campanha.pontosAcumulados.toLocaleString('pt-BR')}</p>
                              </div>
                              <div className="bg-green-50 rounded-lg p-2">
                                <p className="text-xs text-green-700 font-medium">Meta</p>
                                <p className="text-sm font-bold text-green-900">{campanha.metaAtingida}%</p>
                              </div>
                              <div className="bg-purple-50 rounded-lg p-2">
                                <p className="text-xs text-purple-700 font-medium">Ranking</p>
                                <p className="text-sm font-bold text-purple-900">#{campanha.posicaoRanking}</p>
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                              <span className="flex items-center space-x-1">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-task-line"></i>
                                </div>
                                <span>Missões: {campanha.missoesConcluidas}/{campanha.missoesTotais}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-customer-service-line"></i>
                                </div>
                                <span>Atendimentos: {campanha.atendimentos}</span>
                              </span>
                            </div>

                            {/* NOVAS MÉTRICAS: Convites, Comunicação e Contratos */}
                            <div className="flex items-center gap-3 text-xs border-t border-gray-100 pt-3 mt-3">
                              {/* Convites */}
                              <div className="flex items-center gap-1.5 text-teal-700">
                                <i className="ri-mail-send-line text-sm" title="Convites"></i>
                                <span className="font-medium" title="Total de convites enviados">{campanha.convites.enviados}</span>
                                <i className="ri-checkbox-circle-line text-teal-600" title="Convites aceitos"></i>
                                <span className="text-teal-600">env.</span>
                                <span className="font-medium" title="Total de convites aceitos">{campanha.convites.aceitos}</span>
                                <span className="text-teal-600">aceit.</span>
                              </div>

                              {/* Separador */}
                              <div className="w-px h-4 bg-gray-300"></div>

                              {/* Comunicação */}
                              <div className="flex items-center gap-1.5 text-indigo-700">
                                <i className="ri-message-3-line text-sm" title="Comunicações"></i>
                                <span className="font-medium" title="Total de comunicações abertas">{campanha.comunicacao.abertura}</span>
                                <i className="ri-eye-line text-indigo-600" title="Aberturas"></i>
                                <span className="text-indigo-600">abert.</span>
                                <i className="ri-reply-line text-indigo-600" title="Respostas"></i>
                                <span className="font-medium" title="Total de respostas recebidas">{campanha.comunicacao.resposta}</span>
                                <span className="text-indigo-600">resp.</span>
                              </div>

                              {/* Separador */}
                              <div className="w-px h-4 bg-gray-300"></div>

                              {/* Contratos */}
                              <div className="flex items-center gap-1.5 text-emerald-700">
                                <i className="ri-file-text-line text-sm" title="Contratos"></i>
                                <span className="font-medium" title="Total de contratos assinados">{campanha.contratos.assinados}</span>
                                <i className="ri-checkbox-circle-line text-emerald-600" title="Assinados"></i>
                                <span className="text-emerald-600">assin.</span>
                                <i className="ri-time-line text-yellow-600" title="Pendentes"></i>
                                <span className="font-medium" title="Total de contratos pendentes">{campanha.contratos.pendentes}</span>
                                <span className="text-yellow-600">pend.</span>
                              </div>
                            </div>

                            {campanha.distintivos.length > 0 && (
                              <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
                                {campanha.distintivos.map((distintivo, idx) => (
                                  <div key={idx} className="flex items-center space-x-1 bg-gray-50 px-2 py-1 rounded text-xs">
                                    <div className="w-3 h-3 flex items-center justify-center">
                                      <i className={`${distintivo.icone} ${distintivo.cor}`}></i>
                                    </div>
                                    <span className="text-gray-700">{distintivo.nome}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Timeline de Premiações (30 dias) */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-gift-line text-green-600"></i>
                        </div>
                        <span>Premiações Recebidas</span>
                      </h4>
                      <span className="text-sm font-medium text-gray-600">Últimos 30 dias</span>
                    </div>

                    {!participanteSelecionado.premiacoes || participanteSelecionado.premiacoes.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className="ri-gift-line text-gray-400"></i>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">Nenhuma premiação nos últimos 30 dias</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {/* Resumo por Tipo */}
                        <div className="grid grid-cols-2 gap-2 mb-4 pb-4 border-b border-gray-200">
                          {Array.from(new Set(participanteSelecionado.premiacoes.map(p => p.tipo))).map((tipo) => {
                            const total = participanteSelecionado.premiacoes
                              .filter(p => p.tipo === tipo)
                              .reduce((sum, p) => sum + p.valor, 0);
                            const quantidade = participanteSelecionado.premiacoes.filter(p => p.tipo === tipo).length;
                            
                            return (
                              <div key={tipo} className={`border rounded-lg p-3 ${getTipoPremiacaoColor(tipo)}`}>
                                <div className="flex items-center space-x-2 mb-1">
                                  <div className="w-4 h-4 flex items-center justify-center">
                                    <i className={getTipoPremiacaoIcon(tipo)}></i>
                                  </div>
                                  <span className="text-xs font-semibold">{tipo}</span>
                                </div>
                                <p className="text-lg font-bold">R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                <p className="text-xs opacity-75">{quantidade} {quantidade === 1 ? 'premiação' : 'premiações'}</p>
                              </div>
                            );
                          })}
                        </div>

                        {/* Timeline */}
                        <div className="relative">
                          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                          
                          {participanteSelecionado.premiacoes
                            .sort((a, b) => b.data.getTime() - a.data.getTime())
                            .map((premiacao, index) => (
                            <div key={premiacao.id} className="relative pl-14 pb-6 last:pb-0">
                              <div className={`absolute left-3 w-6 h-6 rounded-full border-4 border-white flex items-center justify-center ${
                                premiacao.status === 'Entregue' ? 'bg-green-500' :
                                premiacao.status === 'Processado' ? 'bg-yellow-500' :
                                'bg-orange-500'
                              }`}>
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className={`${getTipoPremiacaoIcon(premiacao.tipo)} text-white text-xs`}></i>
                                </div>
                              </div>

                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getTipoPremiacaoColor(premiacao.tipo)}`}>
                                        {premiacao.tipo}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusPremiacaoColor(premiacao.status)}`}>
                                        {premiacao.status}
                                      </span>
                                    </div>
                                    <p className="text-sm font-semibold text-gray-900">{premiacao.descricao}</p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      <span className="flex items-center space-x-1">
                                        <div className="w-3 h-3 flex items-center justify-center">
                                          <i className="ri-megaphone-line"></i>
                                        </div>
                                        <span>{premiacao.campanha}</span>
                                      </span>
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-green-600">
                                      R$ {premiacao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {premiacao.data.toLocaleDateString('pt-BR')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Total Geral */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 mt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-green-800">Total em Premiações (30 dias)</p>
                              <p className="text-xs text-green-600 mt-1">
                                {participanteSelecionado.premiacoes.length} {participanteSelecionado.premiacoes.length === 1 ? 'premiação recebida' : 'premiações recebidas'}
                              </p>
                            </div>
                            <p className="text-3xl font-bold text-green-700">
                              R$ {participanteSelecionado.premiacoes.reduce((sum, p) => sum + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={() => handleEdit(participanteSelecionado.id)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-edit-line"></i>
                  </div>
                  <span>Editar Participante</span>
                </button>
                <button
                  onClick={() => setShowDetalhesModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Ticket com Participante Pré-selecionado */}
        {showTicketModal && (
          <TicketModal
            onClose={() => {
              setShowTicketModal(false);
              setParticipanteParaAtendimento(null);
            }}
            participantePre={participanteParaAtendimento}
          />
        )}

        {/* Modal de Confirmação de Exclusão */}
        {showDeleteConfirmModal && participanteParaExcluir && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-delete-bin-line text-red-600 text-2xl"></i>
                    </div>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Excluir Participante</h3>
                <p className="text-sm text-gray-600 text-center mb-1">
                  Tem certeza que deseja excluir o participante abaixo?
                </p>
                <p className="text-sm font-semibold text-gray-900 text-center mb-4">
                  "{participanteParaExcluir.nome}"
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-6">
                  <div className="flex items-start space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-alert-line text-orange-600 text-sm"></i>
                    </div>
                    <p className="text-xs text-orange-800">
                      Esta ação é <strong>irreversível</strong>. Todos os dados do participante, incluindo pontos, campanhas vinculadas e histórico de premiações serão perdidos permanentemente.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setParticipanteParaExcluir(null);
                    }}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm font-medium flex items-center justify-center space-x-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line"></i>
                    </div>
                    <span>Sim, Excluir</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
